import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import { Container, Card, Row, Col, Button, Modal, ListGroup, Badge } from 'react-bootstrap';
import { FaCheckCircle, FaTimesCircle, FaArrowLeft, FaShoppingCart, FaWhatsapp } from 'react-icons/fa';
import styles from '../styles/CardDetails.module.css';
import ecardApi from '../../api/eCards/ecardApi';
import SpinnerLoader from '../../components/Buttons/SpinnerLoader';

const CardDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { card: stateCard } = location.state || {};

  const [card, setCard] = useState(stateCard || null);
  const [loading, setLoading] = useState(!stateCard);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchCard = async () => {
      setLoading(true);
      try {
        const response = await ecardApi.getTemplate(id);
        const cardDetails = response?.data?.data || response?.data || response || {};
        if (cardDetails && typeof cardDetails === 'object' && Object.keys(cardDetails).length > 0) {
          setCard(cardDetails);
        }
      } catch (error) {
        console.error("Failed to fetch card details", error);
      } finally {
        setLoading(false);
      }
    };
    if (id) {
      fetchCard();
    }
  }, [id]);

  if (loading && !card) {
    return (
      <Container className="py-5 text-center" style={{ marginTop: '100px' }}>
        <SpinnerLoader />
      </Container>
    );
  }

  if (!card) {
    return (
      <Container className="py-5 text-center" style={{ marginTop: '100px' }}>
        <h4>Card not found</h4>
        <Button variant="primary" onClick={() => navigate(-1)} className="mt-3">Go Back</Button>
      </Container>
    );
  }

  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  // Use the new API payload structure
  const imagePath = card.preview_path || card.photo_path || card.lg_path || card.wallpaper;
  const priceInt = parseInt(card.price) || 0;

  // Feature logic based on the pricing tier image provided
  const features = [
    { name: "Dynamic Details & Card Number", included: true },
    { name: "Sending card link via SMS", included: true },
    { name: "Sending card via Email", included: true },
    { name: "Sent List Report (PDF)", included: true },
    { name: "Sending card via WhatsApp", included: priceInt >= 750 },
    { name: "QR Code", included: priceInt >= 750 },
    { name: "Custom Template", included: priceInt >= 750 },
    { name: "RSVP Link", included: priceInt >= 750 },
    { name: "Bride/Groom Photo", included: priceInt >= 950 },
    { name: "Auto Thank you Message", included: priceInt >= 950 },

  ];

  return (
    <Container className={styles.container} style={{ marginTop: '100px', marginBottom: '50px' }}>
      <Button variant="link" className="text-decoration-none mb-4 ps-0" onClick={() => navigate(-1)}>
        <FaArrowLeft className="me-2" /> Back to E-Cards
      </Button>

      <Row className="g-5 mb-5">
        <Col md={6} xs={12}>
          <div className="position-relative shadow-lg rounded d-flex align-items-center justify-content-center bg-light" style={{ cursor: 'pointer', overflow: 'hidden', padding: '80px' }} onClick={handleOpenModal}>
            <img
              src={imagePath}
              alt={card.name}
              className="img-fluid rounded"
              style={{ maxHeight: '550px', objectFit: 'contain', transition: 'transform 0.3s ease' }}
              onMouseOver={e => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
            />
            <div className="position-absolute bottom-0 w-100 p-3 text-center" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.6))' }}>
              <span className="text-white small"><i className="fas fa-search-plus me-1"></i> Click to enlarge</span>
            </div>
          </div>
        </Col>

        <Col md={6} xs={12}>
          <div className="d-flex flex-column h-100">
            <h2 className="mb-2" style={{ color: '#24366b', fontWeight: '700' }}>{card.name}</h2>
            <div className="d-flex align-items-center gap-3 mb-4 flex-wrap">
              <Badge bg="primary" className="fs-6 py-2 px-3 rounded-pill">{card.card_category}</Badge>
              <Badge bg="secondary" className="fs-6 py-2 px-3 rounded-pill">{card.event_type}</Badge>
              <h3 className="mb-0 text-success ms-auto fw-bold">TZS {priceInt.toLocaleString()}</h3>
            </div>

            <Card className="border-0 shadow-sm flex-grow-1 d-flex flex-column">
              <Card.Header className="bg-white border-bottom-0 pt-4 pb-0">
                <h5 className="fw-bold mb-0 text-muted text-uppercase" style={{ letterSpacing: '1px' }}>Features Included</h5>
              </Card.Header>
              <Card.Body className="flex-grow-1 overflow-auto">
                <ListGroup variant="flush">
                  {features.map((feature, idx) => (
                    <ListGroup.Item key={idx} className="border-0 px-0 py-2 d-flex align-items-center">
                      {feature.included ? (
                        <FaCheckCircle className="text-success me-3 fs-5 flex-shrink-0" />
                      ) : (
                        <FaTimesCircle className="text-muted me-3 fs-5 flex-shrink-0" style={{ opacity: 0.3 }} />
                      )}
                      <span className={feature.included ? "fw-medium" : "text-muted text-decoration-line-through"} style={{ fontSize: '1.05rem' }}>
                        {feature.name}
                      </span>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card.Body>
              <Card.Footer className="bg-white border-top-0 pb-4 mt-auto">
                {card.template_type === 'general' ? (
                  <Link
                    to="/dashboard"
                    className="btn w-100 rounded-pill py-3 fw-bold shadow-sm d-flex align-items-center justify-content-center"
                    style={{ backgroundColor: '#10b981', borderColor: '#10b981', color: 'white', fontSize: '1.1rem', textDecoration: 'none' }}
                  >
                    <FaCheckCircle className="me-2 fs-4" /> Use It / Generate Cards
                  </Link>
                ) : (
                  <a
                    href={`https://wa.me/255747636516?text=I%20need%20customization%20on%20this%20card:%20${encodeURIComponent(card.name)}%20(Price:%20TZS%20${priceInt.toLocaleString()}).`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn w-100 rounded-pill py-3 fw-bold shadow-sm d-flex align-items-center justify-content-center"
                    style={{ backgroundColor: '#4169e1', borderColor: '#4169e1', color: 'white', fontSize: '1.1rem' }}
                  >
                    <FaWhatsapp className="me-2 fs-4" /> Request Card Customization
                  </a>
                )}
              </Card.Footer>
            </Card>
          </div>
        </Col>
      </Row>

      {/* Fullscreen Image Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{card.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center bg-light">
          <img
            src={imagePath}
            alt={card.name}
            className="img-fluid rounded shadow"
            style={{ maxHeight: '80vh', objectFit: 'contain' }}
          />
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default CardDetails;
